import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
  TextField, MenuItem, IconButton, Button, Typography,
  FormControlLabel, Checkbox,
} from '@material-ui/core';
import {Close as CloseIcon, Delete as DeleteIcon} from '@material-ui/icons';
import * as A from '../store/lineageActions';
import {expandViewLineage} from '../store/lineageActions';
import {APP_TYPES} from '../util/theme';
import {resolveIcon} from '../util/characteristicIcons';
import BufferedTextField from './BufferedTextField';
import ConnectionPicker from './ConnectionPicker';
import DdSchemaPicker from './DdSchemaPicker';
import DdObjectPicker from './DdObjectPicker';
import DdColumnPicker from './DdColumnPicker';
import NodeRefMultiPicker from './NodeRefMultiPicker';

/**
 * Right-side drawer to edit the selected node.
 * Adds: Source/Dest switches, Characteristic select, Map-to radio (none/connection/dd_schema),
 * connection branch (ConnectionPicker + free-text tables/elements),
 * DD branch (DdSchemaPicker → per-table DdObjectPicker + DdColumnPicker per element),
 * and per-element CDE checkbox when isDestination.
 */
class NodeInspector extends Component {
  set = (key, value) => {
    const {activeNodeId, updateNode, node} = this.props;
    if (!node) return;
    updateNode(activeNodeId, {data: {...node.data, [key]: value}});
  };

  setDetail = (key, value) => {
    const {activeNodeId, updateNode, node} = this.props;
    if (!node) return;
    updateNode(activeNodeId, {data: {...node.data, details: {...(node.data.details || {}), [key]: value}}});
  };

  setMapping = (key, value) => {
    const {activeNodeId, updateNode, node} = this.props;
    if (!node) return;
    const mapping = {...((node.data && node.data.mapping) || {type: 'none', connectionId: null, schemaId: null}), [key]: value};
    updateNode(activeNodeId, {data: {...node.data, mapping}});
  };

  /**
   * Set a mapping field and, when a label is derivable from the picked target
   * (connection name / DD schema name) and the node has no label yet, name the node
   * after it — in one dispatch so it's a single history step.
   */
  setMappingValue = (key, value, derivedLabel) => {
    const {activeNodeId, updateNode, node} = this.props;
    if (!node) return;
    const mapping = {...((node.data && node.data.mapping) || {type: 'none', connectionId: null, schemaId: null}), [key]: value};
    const data = {...node.data, mapping};
    if (derivedLabel && !(node.data.label || '').trim()) data.label = derivedLabel;
    updateNode(activeNodeId, {data});
  };

  /** Source and Destination are mutually exclusive — a node is a source, a destination, or neither. */
  setSrcDest = (value) => {
    const {activeNodeId, updateNode, node} = this.props;
    if (!node) return;
    updateNode(activeNodeId, {data: {
      ...node.data,
      isSource: value === 'source',
      isDestination: value === 'destination',
    }});
  };

  render() {
    const {
      drawerOpen, node, activeNodeId, closeDrawer,
      addElement, updateElement, removeElement,
      addTable, updateTable, removeTable,
      characteristics, ddSchemas, connections, lineageViewType, activeTableId,
    } = this.props;
    if (!drawerOpen || !node || node.kind === 'group') return null;

    const data = node.data || {};
    const elements = Array.isArray(data.dataElements) ? data.dataElements : [];
    const isDatabase = (data.appType || 'application') === 'database';
    const tables = Array.isArray(data.tables) ? data.tables : [];
    const childWord = isDatabase ? 'table' : 'child node';
    const childWordPlural = isDatabase ? 'Tables' : 'Child nodes';

    const mapping = data.mapping || {type: 'none', connectionId: null, schemaId: null};
    const mapType = mapping.type || 'none';
    const isDestination = !!data.isDestination;
    const srcDest = data.isSource ? 'source' : (isDestination ? 'destination' : 'none');

    // Child-focused view: in DATA view, selecting a non-database node's child card scopes the
    // inspector to that child — its name + its data elements (add/edit/delete), no child-node
    // management. Databases keep their per-table column pickers in the normal parent inspector.
    const childTable = (!isDatabase && lineageViewType === 'data' && activeTableId) ?
      tables.find((t) => t.id === activeTableId) : null;
    if (childTable) {
      const els = Array.isArray(childTable.dataElements) ? childTable.dataElements : [];
      return (
        <div className="ln-inspector" data-testid="node-inspector">
          <div className="ln-inspector-head">
            <Typography variant="subtitle1">Child node</Typography>
            <IconButton size="small" onClick={closeDrawer} data-testid="inspector-close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className="ln-inspector-section ln-inspector-section--first">
            <Typography variant="caption" color="textSecondary">in {data.label || data.appType || 'node'}</Typography>
            <BufferedTextField
              label="Child node name" fullWidth margin="dense" value={childTable.name || ''}
              onChange={(e) => updateTable(activeNodeId, childTable.id, {name: e.target.value})}
              data-testid="inspector-child-name"
            />
          </div>
          <div className="ln-inspector-section">
            <Typography variant="subtitle2">Data elements ({els.length})</Typography>
            {els.map((el) => (
              <div key={el.id} className="ln-inspector-el">
                <BufferedTextField
                  margin="dense" placeholder="name" value={el.name || ''}
                  onChange={(e) => updateElement(activeNodeId, el.id, {name: e.target.value, displayName: e.target.value}, childTable.id)}
                />
                <BufferedTextField
                  margin="dense" placeholder="type" value={el.dataType || ''} className="ln-inspector-el-type"
                  onChange={(e) => updateElement(activeNodeId, el.id, {dataType: e.target.value}, childTable.id)}
                />
                {isDestination && (
                  <Checkbox
                    checked={!!el.isCde}
                    onChange={(e) => updateElement(activeNodeId, el.id, {isCde: e.target.checked}, childTable.id)}
                    size="small" title="Critical Data Element"
                    inputProps={{'data-testid': `el-cde-${el.id}`}}
                  />
                )}
                <IconButton size="small" onClick={() => removeElement(activeNodeId, el.id, childTable.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            <Button
              size="small"
              onClick={() => addElement(activeNodeId, {name: '', displayName: ''}, childTable.id)}
              data-testid="inspector-add-element"
            >
              + Add element
            </Button>
          </div>
        </div>
      );
    }

    const connName = (id) => {
      const c = (connections || []).find((x) => (x.idconnectiondetail || x.id) === id);
      return c ? (c.connection_name || c.name || '') : '';
    };
    const schemaName = (id) => {
      const s = (ddSchemas || []).find((x) => (x.schemaId || x.id) === id);
      return s ? (s.schemaName || s.name || '') : '';
    };

    return (
      <div className="ln-inspector" data-testid="node-inspector">
        <div className="ln-inspector-head">
          <Typography variant="subtitle1">Node details</Typography>
          <IconButton size="small" onClick={closeDrawer} data-testid="inspector-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* MAPPING */}
        <div className="ln-inspector-section ln-inspector-section--first">
          <div className="ln-inspector-section-title">Mapping</div>
          {isDatabase ? (
            <>
              <TextField
                select label="Map to" fullWidth margin="dense"
                value={mapType}
                onChange={(e) => this.setMapping('type', e.target.value)}
                inputProps={{'data-testid': 'inspector-map-type'}}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="connection">Connection</MenuItem>
                <MenuItem value="dd_schema">DD schema</MenuItem>
              </TextField>

              {mapType === 'connection' && (
                <ConnectionPicker
                  value={mapping.connectionId || ''}
                  onChange={(id) => this.setMappingValue('connectionId', id, connName(id))}
                  label="Database connection"
                />
              )}

              {mapType === 'dd_schema' && (
                <DdSchemaPicker
                  value={mapping.schemaId || ''}
                  onChange={(id) => this.setMappingValue('schemaId', id, schemaName(id))}
                />
              )}
            </>
          ) : (
            <NodeRefMultiPicker node={node} activeNodeId={activeNodeId} />
          )}
        </div>

        {/* GENERAL */}
        <div className="ln-inspector-section">
          <div className="ln-inspector-section-title">General</div>
          <BufferedTextField
            label="Label" fullWidth margin="dense" value={data.label || ''}
            onChange={(e) => this.set('label', e.target.value)}
            data-testid="inspector-label"
          />
          <TextField
            select label="Application type" fullWidth margin="dense"
            value={data.appType || 'application'}
            onChange={(e) => {
              const v = e.target.value;
              const {activeNodeId, updateNode} = this.props;
              updateNode(activeNodeId, {data: {
                ...node.data,
                appType: v,
                details: {...(node.data.details || {}), nodeRefIds: []},
                ...(v !== 'database' ? {mapping: {type: 'none', connectionId: null, schemaId: null}} : {}),
              }});
            }}
          >
            {APP_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <BufferedTextField
            label="Description" fullWidth multiline margin="dense" value={data.description || ''}
            onChange={(e) => this.set('description', e.target.value)}
          />
          <BufferedTextField
            label="Environment" fullWidth margin="dense"
            value={(data.details && data.details.environment) || ''}
            onChange={(e) => this.setDetail('environment', e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!(data.details && data.details.isExternal)}
                onChange={(e) => this.setDetail('isExternal', e.target.checked)}
                inputProps={{'data-testid': 'inspector-external'}}
              />
            }
            label="External"
          />
        </div>

        {/* CLASSIFICATION */}
        <div className="ln-inspector-section">
          <div className="ln-inspector-section-title">Classification</div>
          {/* Role — Source / Destination mutually exclusive (a node is one, or neither) */}
          <TextField
            select label="Role" fullWidth margin="dense"
            value={srcDest}
            onChange={(e) => this.setSrcDest(e.target.value)}
            inputProps={{'data-testid': 'inspector-src-dest'}}
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="source" data-testid="inspector-role-source">Source</MenuItem>
            <MenuItem value="destination" data-testid="inspector-role-destination">Destination</MenuItem>
          </TextField>

          {/* Characteristic select — TextField select matches Map-to and Application-type heights */}
          <TextField
            select label="Characteristic" fullWidth margin="dense"
            value={data.characteristicId || ''}
            onChange={(e) => this.set('characteristicId', e.target.value || null)}
            inputProps={{'data-testid': 'inspector-characteristic'}}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {characteristics.filter((c) => c.isActive !== false && c.is_active !== 0).map((c) => {
              const IconCmp = resolveIcon(c.icon);
              return (
                <MenuItem key={c.id} value={c.id}>
                  <IconCmp style={{fontSize: 16, marginRight: 6, color: c.color || '#1976d2', verticalAlign: 'middle'}} />
                  {c.name}
                </MenuItem>
              );
            })}
          </TextField>
        </div>

        {/* Tables / Child-nodes section — hidden in non-database DATA view (there a child's data
            elements are edited via its card / child-focus, not child-node management). */}
        {(isDatabase || lineageViewType === 'system') ? (
        <div className="ln-inspector-section">
          <Typography variant="subtitle2">{childWordPlural} ({tables.length})</Typography>
          <Typography variant="caption" color="textSecondary">
            In data view this node splits into one card per {childWord}.
          </Typography>
          {tables.map((t) => (
            <div key={t.id} className="ln-inspector-table-slot">
              {isDatabase && mapType === 'dd_schema' && mapping.schemaId ? (
                /* DD-mapped: the schema-object dropdown IS the table selector (table name = object name) */
                <div style={{flex: 1}}>
                  <DdObjectPicker
                    schemaId={mapping.schemaId}
                    value={t.objectId || ''}
                    onChange={({objectId, objectName, objectType}) => {
                      updateTable(activeNodeId, t.id, {objectId, objectName, objectType, name: objectName});
                      if (objectType === 'VIEW' && objectId) {
                        const schema = ddSchemas.find((s) => s.schemaId === mapping.schemaId || s.id === mapping.schemaId);
                        const instId = schema ? (schema.instanceId || schema.instance_id || null) : null;
                        this.props.expandViewLineage(activeNodeId, t.id, objectId, objectName, instId);
                      }
                    }}
                    label={childWord}
                  />
                </div>
              ) : (
                /* free-text name (connection / none mapping, or non-database node) */
                <BufferedTextField
                  margin="dense" placeholder={`${childWord} name`} value={t.name || ''}
                  onChange={(e) => updateTable(activeNodeId, t.id, {name: e.target.value})}
                  style={{flex: 1}}
                />
              )}
              {/* Column pickers for elements inside this table — DATA view only, database nodes only. */}
              {isDatabase && lineageViewType === 'data' && (t.dataElements || []).length > 0 && mapType === 'dd_schema' && t.objectId && (
                <div style={{paddingLeft: 8}}>
                  {(t.dataElements || []).map((el) => (
                    <div key={el.id} style={{display: 'flex', alignItems: 'center', gap: 4}}>
                      <span className="ln-inspector-el-colname">
                        {el.displayName || el.name}
                      </span>
                      <div style={{flex: 1}}>
                        <DdColumnPicker
                          objectId={t.objectId}
                          value={el.columnId || ''}
                          onChange={({columnId, columnName, dataType}) => {
                            const upd = {columnId, columnName: columnName || el.name, dataType: dataType || el.dataType};
                            updateElement(activeNodeId, el.id, upd, t.id);
                          }}
                          label="Column"
                        />
                      </div>
                      {isDestination && (
                        <Checkbox
                          checked={!!el.isCde}
                          onChange={(e) => updateElement(activeNodeId, el.id, {isCde: e.target.checked}, t.id)}
                          size="small"
                          title="Critical Data Element"
                          inputProps={{'data-testid': `el-cde-${el.id}`}}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <IconButton size="small" onClick={() => removeTable(activeNodeId, t.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
          <Button
            size="small"
            onClick={() => addTable(activeNodeId)}
            data-testid="inspector-add-table"
          >
            + Add {childWord}
          </Button>
        </div>
        ) : tables.length > 0 ? (
          <div className="ln-inspector-section">
            <Typography variant="caption" color="textSecondary">
              Select a child node card in the canvas to edit its data elements.
            </Typography>
          </div>
        ) : null}

        {/* Direct data elements — non-database nodes with no child nodes only, data view only.
            A database always holds tables; its elements are added per-table in data view. */}
        {tables.length === 0 && !isDatabase && lineageViewType === 'data' && (
          <div className="ln-inspector-section">
            <Typography variant="subtitle2">Data elements ({elements.length})</Typography>
            {elements.map((el) => (
              <div key={el.id} className="ln-inspector-el">
                <BufferedTextField
                  margin="dense" placeholder="name" value={el.name || ''}
                  onChange={(e) => updateElement(activeNodeId, el.id, {name: e.target.value, displayName: e.target.value})}
                />
                <BufferedTextField
                  margin="dense" placeholder="type" value={el.dataType || ''} className="ln-inspector-el-type"
                  onChange={(e) => updateElement(activeNodeId, el.id, {dataType: e.target.value})}
                />
                {/* CDE checkbox — only when isDestination */}
                {isDestination && (
                  <Checkbox
                    checked={!!el.isCde}
                    onChange={(e) => updateElement(activeNodeId, el.id, {isCde: e.target.checked})}
                    size="small"
                    title="Critical Data Element"
                    inputProps={{'data-testid': `el-cde-${el.id}`}}
                  />
                )}
                <IconButton size="small" onClick={() => removeElement(activeNodeId, el.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            <Button
              size="small"
              onClick={() => addElement(activeNodeId, {name: '', displayName: ''})}
              data-testid="inspector-add-element"
            >
              + Add element
            </Button>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const {activeNodeId, activeTableId, drawerOpen, map, characteristics, ddSchemas, connections, lineageViewType} = state.lineageReducer;
  return {
    drawerOpen,
    activeNodeId,
    activeTableId,
    node: activeNodeId ? map.nodes.find((n) => n.id === activeNodeId) : null,
    characteristics: characteristics || [],
    ddSchemas: ddSchemas || [],
    connections: connections || [],
    lineageViewType,
  };
};

export default connect(mapStateToProps, {
  updateNode: A.updateNode,
  addElement: A.addElement,
  updateElement: A.updateElement,
  removeElement: A.removeElement,
  addTable: A.addTable,
  updateTable: A.updateTable,
  removeTable: A.removeTable,
  closeDrawer: A.closeDrawer,
  expandViewLineage,
})(NodeInspector);
